/**
 * Datums- und Zeitformate für die Anzeige, einmal für Web und App.
 *
 * Bis zum 25.09.2026 formatierte jede App-Seite ihr Datum selbst (zwölf eigene
 * `toLocaleDateString`-Aufrufe in `mobile/app/**`), das Web hatte `frontend/src/utils/format.ts`.
 * Im Bestand standen dadurch drei Schreibweisen für dasselbe Datum („05.09.2026", „5.9.2026",
 * „05. Sept."), und zwei App-Seiten parsten reine Kalendertage als UTC. Hier liegt die eine
 * Fassung; das Web re-exportiert sie aus `utils/format.ts`.
 *
 * Der Ersatz für einen fehlenden Wert bleibt ein Parameter: er ist eine Entscheidung der
 * jeweiligen Tabelle oder Zeile, nicht des Formats.
 */

const LOCALE = 'de-DE';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Reine Kalendertage (`YYYY-MM-DD`) werden als lokale Mitternacht gelesen — ohne `T00:00:00`
 * deutet die JS-Engine sie als UTC und zeigt westlich von Greenwich den Vortag. Zeitstempel
 * (`2026-09-05T14:03:11Z`) gehen unverändert durch. `null` bei leerem oder unlesbarem Wert.
 */
export function parseDisplayDate(value?: string | null): Date | null {
  if (!value) return null;
  const d = new Date(DATE_ONLY.test(value) ? `${value}T00:00:00` : value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const format = (opts: Intl.DateTimeFormatOptions) =>
  (value?: string | null, fallback = ''): string => {
    const d = parseDisplayDate(value);
    return d ? d.toLocaleDateString(LOCALE, opts) : fallback;
  };

/** „05. September 2026". */
export const formatDayLong = format({ day: '2-digit', month: 'long', year: 'numeric' });

/** „05.09.2026". */
export const formatDayShort = format({ day: '2-digit', month: '2-digit', year: 'numeric' });

/** „Samstag, 05. September 2026" — Kopfzeile von Event und Etappe. */
export const formatDayWeekday = format({ weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

/** „05.09." — in Listen, deren Jahr aus dem Zusammenhang klar ist (Etappen eines Rennens). */
/** „Mi., 13. Jan.“ — kurz genug für Listenzeilen (Winterchallenge). */
export const formatDayWeekdayShort = format({ weekday: 'short', day: 'numeric', month: 'short' });

export const formatDayMonth = format({ day: '2-digit', month: '2-digit' });

/** „1. November“ — Tag und Monat ausgeschrieben, für Fließtext (Winterchallenge: Saisonstart). */
export const formatDayMonthLong = format({ day: 'numeric', month: 'long' });

/** „Januar“ — nur der Monatsname (Winterchallenge: „Im Januar“). */
export const formatMonthLong = format({ month: 'long' });

/** Zeitstempel als „05.09.2026, 14:03:11". */
export const formatDateTime = (iso?: string | null, fallback = ''): string => {
  const d = parseDisplayDate(iso);
  return d ? d.toLocaleString(LOCALE) : fallback;
};

/**
 * Zeitraum zweier Kalendertage: „04.–26.07.2026" im selben Monat, „04.07.–02.08.2026"
 * monatsübergreifend, „30.12.2026–03.01.2027" über den Jahreswechsel, „12.04.2026" an einem Tag.
 */
export function formatDayRange(start: string, end: string): string {
  const s = parseDisplayDate(start);
  const e = parseDisplayDate(end);
  if (!s || !e) return formatDayShort(start || end);
  if (start === end) return formatDayShort(start);
  if (s.getFullYear() !== e.getFullYear()) return `${formatDayShort(start)}–${formatDayShort(end)}`;
  if (s.getMonth() === e.getMonth()) return `${String(s.getDate()).padStart(2, '0')}.–${formatDayShort(end)}`;
  return `${formatDayMonth(start)}–${formatDayShort(end)}`;
}
